var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useRef, useState } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import CalendarIcon from '@material-ui/icons/CalendarToday';
import InputAdornment from '@material-ui/core/InputAdornment';
import { DatePicker } from '@material-ui/pickers';
import TextField from './TextField';
import Typography from '@material-ui/core/Typography';
import { _ParentFrame } from '../../src/Frame/types/Frame';
import Select from './Select';
import { _T } from '../appI18n';
const useStyles = makeStyles(theme => ({
    root: {
        position: 'relative',
        display: 'inline-block',
    },
    adornment: {
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingRight: theme.spacing(1),
        pointerEvents: 'none',
    },
    inputBorderless: {
        '& .MuiInputBase-root': {
            border: 0,
            borderRadius: 0,
        },
    },
    fullWidth: { width: '100%' },
    toolbar: {
        background: theme.palette.primary.main,
        // FIXME: hardcoded color.
        color: '#ffe5dd',
        padding: `${theme.spacing(3)}px ${theme.spacing(2)}px`,
    },
    toolbarTitle: {
        // FIXME: hardcoded color.
        color: '#ffc8b5',
        fontSize: '1.1rem',
    },
    durationSelector: {
        marginTop: theme.spacing(2),
    },
}));
function convertTourAvailabilitiesToAvailabilityMap(availabilities) {
    return availabilities.reduce((acc, x) => {
        const date = moment(x.checkInDate).format('YYYY-MM-DD');
        const month = date.substr(0, 7);
        const target = !(month in acc) ? (acc[month] = {}) : acc[month];
        target[date] = x;
        return acc;
    }, {});
}
export function TourAvailabilityDatePicker(props) {
    return AvailabilityDatePicker(props);
}
const AvailabilityDatePicker = React.forwardRef((props, ref) => {
    var _a;
    const [status, setStatus] = useState('none');
    const [availabilityDates, setAvailabilityDates] = useState({});
    const [availabilityDurations, setAvailabilityDurations] = useState([]);
    const [open, setOpen] = useState(false);
    const [tempDuration, setTempDuration] = useState(props.duration);
    const classes = useStyles();
    const renderInput = useCallback((params) => {
        var _a, _b, _c;
        return React.createElement(TextField, { label: props.fieldLabel, masqueradeAsSelect: true, className: `${((_a = props.inputProps) === null || _a === void 0 ? void 0 : _a.borderless) ? classes.inputBorderless : ''} ${((_b = props.classes) === null || _b === void 0 ? void 0 : _b.input) || ''}`, value: params.value, disabled: params.disabled, onClick: params.onClick, ref: params.inputRef, fullWidth: true, inputProps: { readOnly: true }, InputLabelProps: { className: `${((_c = props.labelProps) === null || _c === void 0 ? void 0 : _c.className) || ''}` }, InputProps: {
                startAdornment: React.createElement(InputAdornment, { position: "start" },
                    React.createElement(CalendarIcon, null)),
            } });
    }, [props.fieldLabel]);
    const dialogRef = useRef(null);
    const durationRef = useRef(null);
    const renderToolbar = ({ date }) => {
        var _a, _b;
        return React.createElement("div", { className: classes.toolbar },
            React.createElement("div", { className: classes.toolbarTitle }, props.calendarLabel),
            React.createElement(Typography, { variant: "h4", component: "div" }, date === null || date === void 0 ? void 0 : date.format('LL')),
            props.withDuration ?
                React.createElement(Select, { label: _T.COMMON_NIGHTS, value: tempDuration, fullWidth: true, onChange: (e) => setTempDuration(Number.parseInt(e.target.value)), color: "primary", ref: durationRef, formControlClassName: classes.durationSelector }, (_b = ((_a = availabilityDurations.find(d => d.checkInDate == props.date)) !== null && _a !== void 0 ? _a : availabilityDurations[0])) === null || _b === void 0 ? void 0 : _b.durations.map(d => React.createElement("option", { value: d, key: d }, d))) : null);
    };
    const loadAvailability = (monthIso) => __awaiter(void 0, void 0, void 0, function* () {
        setStatus('loading');
        try {
            const availabilities = yield props.onAvailabilityRequested(monthIso);
            if (availabilities.length) {
                setAvailabilityDates(Object.assign(Object.assign({}, availabilityDates), convertTourAvailabilitiesToAvailabilityMap(availabilities)));
                if (props.withDuration && availabilities.length && 'durations' in availabilities[0]) {
                    setAvailabilityDurations(availabilities.map(d => ({ checkInDate: moment(d.checkInDate).format('YYYY-MM-DD'), durations: d.durations })));
                }
            }
            else {
                setAvailabilityDates(Object.assign(Object.assign({}, availabilityDates), { [monthIso]: {} }));
            }
        }
        finally {
            setStatus('ready');
        }
    });
    const handleOpen = () => {
        if (status == 'ready') {
            const parentFrame = _ParentFrame();
            if (props.isFrame && parentFrame) {
                parentFrame.sendMessage({ message: 'open-modal', });
            }
            setOpen(true);
            return;
        }
        if (status == 'loading')
            return;
        const month = (props.date || moment().format('YYYY-MM')).substr(0, 7);
        loadAvailability(month).then(() => {
            const parentFrame = _ParentFrame();
            if (props.isFrame && parentFrame) {
                parentFrame.sendMessage({ message: 'open-modal', });
            }
            setOpen(true);
        });
    };
    const handleClose = () => {
        setOpen(false);
        const parentFrame = _ParentFrame();
        if (props.isFrame && parentFrame) {
            parentFrame.sendMessage({ message: 'close-modal', });
        }
    };
    const handleDateChange = (date) => {
        if (!date)
            throw new Error('Unexpected null date');
        props.onChange(date.format('YYYY-MM-DD'), props.withDuration ? (tempDuration !== null && tempDuration !== void 0 ? tempDuration : availabilityDurations[0].durations[0]) : undefined);
    };
    const handleMonthChange = (date) => __awaiter(void 0, void 0, void 0, function* () {
        if (!date)
            throw new Error('Unexpected null date');
        const monthIso = date.format('YYYY-MM');
        if (monthIso in availabilityDates)
            return;
        yield loadAvailability(monthIso);
    });
    const shouldDisableDate = (date) => {
        if (!date)
            return true;
        const dateIso = date === null || date === void 0 ? void 0 : date.format('YYYY-MM-DD');
        const month = availabilityDates[dateIso.substr(0, 7)];
        const isAvail = (month && !!month[dateIso]) || (props.date && dateIso == props.date);
        return !isAvail;
    };
    return (React.createElement("div", { className: `${props.className || ''} ${classes.root} ${props.fullWidth ? classes.fullWidth : ''}`, ref: ref },
        React.createElement(DatePicker, { autoOk: !props.withDuration, inputVariant: "outlined", disabled: !!props.disabled, format: "LL", maxDate: props.maxDate, shouldDisableDate: shouldDisableDate, disablePast: true, value: props.date, labelFunc: props.labelFunc, onChange: handleDateChange, onMonthChange: handleMonthChange, open: open, onOpen: handleOpen, onClose: handleClose, ToolbarComponent: (_a = props.ToolbarComponent) !== null && _a !== void 0 ? _a : renderToolbar, TextFieldComponent: renderInput, PopoverProps: {
                anchorOrigin: { horizontal: "left", vertical: "bottom" },
                transformOrigin: { horizontal: "left", vertical: "top" },
            }, DialogProps: {
                style: props.isFrame ? {
                    height: 'fit-content'
                } : undefined, ref: dialogRef
            } }),
        React.createElement("div", { className: classes.adornment }, status == 'loading'
            ? React.createElement(CircularProgress, { size: 16 })
            : props.adornment)));
});
export default AvailabilityDatePicker;
